import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, Trash2, Maximize2, Play, File, FileText, Music, Code, Archive, ImageOff, Video, Camera, Square, Cuboid, ChevronDown, ChevronUp } from 'lucide-react';
import { ProjectService } from '@/services/ProjectService';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DxfParser } from 'dxf-parser';
import { generateThumbnail } from '@/utils/thumbnailUtils';

const CameraCapture = ({ onCapture, onClose }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const [isRecording, setIsRecording] = useState(false);
    const [mode, setMode] = useState('photo');
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
    useEffect(() => {
      const calculateDimensions = () => {
          if (!containerRef.current) return;
          
          const container = containerRef.current;
          const containerWidth = container.clientWidth;
          const containerHeight = container.clientHeight;
          const aspectRatio = 16 / 9; // Standard video aspect ratio
          
          let width, height;
          
          // If container is wider than it is tall (landscape)
          if (containerWidth / containerHeight > aspectRatio) {
              height = containerHeight;
              width = height * aspectRatio;
          } else {
              // If container is taller than it is wide (portrait)
              width = containerWidth;
              height = width / aspectRatio;
          }
          
          setDimensions({ width, height });
      };

      // Calculate initial dimensions
      calculateDimensions();

      // Recalculate on window resize
      window.addEventListener('resize', calculateDimensions);

      // Start camera
      startCamera();

      return () => {
          window.removeEventListener('resize', calculateDimensions);
          stopCamera();
      };
  }, []);
  
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: true
        });
        
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        onClose();
      }
    };
  
    const stopCamera = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  
    const capturePhoto = () => {
        if (videoRef.current) {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(videoRef.current, 0, 0);
            
            canvas.toBlob(blob => {
              try {
                if (!blob) {
                  console.error('Failed to create blob from canvas');
                  return;
                }
      
                // Create filename
                const filename = `photo_${Date.now()}.jpg`;
                
                // Create file using the Blob constructor
                let file;
                try {
                  file = new Blob([blob], { type: 'image/jpeg' });
                  // Add properties to make it more File-like
                  file.name = filename;
                  file.lastModified = Date.now();
                } catch (err) {
                  console.error('Error creating file:', err);
                  return;
                }
      
                // Call the capture callback with our modified event
                onCapture({ 
                  target: { 
                    files: [file] 
                  } 
                });
                onClose();
              } catch (err) {
                console.error('Error in blob callback:', err);
              }
            }, 'image/jpeg', 0.95); // Added quality parameter (0.95) for better compression
          } catch (err) {
            console.error('Error capturing photo:', err);
          }
        }
    };
  
    const startRecording = () => {
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(streamRef.current);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
  
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
        onCapture({ target: { files: [file] } });
        onClose();
      };
  
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    };
  
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
          try {
            mediaRecorderRef.current.onstop = () => {
              try {
                const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                const filename = `video_${Date.now()}.webm`;
                
                // Add file-like properties to blob
                blob.name = filename;
                blob.lastModified = Date.now();
      
                onCapture({ 
                  target: { 
                    files: [blob] 
                  } 
                });
                onClose();
              } catch (err) {
                console.error('Error processing video:', err);
              }
            };
      
            mediaRecorderRef.current.stop();
            setIsRecording(false);
          } catch (err) {
            console.error('Error stopping recording:', err);
            setIsRecording(false);
          }
        }
    };
  
    return (
      <div ref={containerRef} className="relative w-full h-[80vh] bg-black flex items-center justify-center">
          <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="object-contain"
              style={{
                  width: dimensions.width,
                  height: dimensions.height,
                  maxWidth: '100%',
                  maxHeight: '100%'
              }}
          />
          
          {/* Controls overlay */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
              <div className="space-y-4 p-4 bg-black bg-opacity-50 rounded-lg">
                  <div className="flex justify-center">
                      <div className="bg-white rounded-full p-1 flex gap-1">
                          <button
                              onClick={() => setMode('photo')}
                              className={`flex items-center px-4 py-2 rounded-full transition-colors ${
                                  mode === 'photo' 
                                      ? 'bg-blue-500 text-white' 
                                      : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                              <Camera size={16} className="mr-2" />
                              Photo
                          </button>
                          <button
                              onClick={() => setMode('video')}
                              className={`flex items-center px-4 py-2 rounded-full transition-colors ${
                                  mode === 'video' 
                                      ? 'bg-blue-500 text-white' 
                                      : 'text-gray-700 hover:bg-gray-100'
                              }`}
                          >
                              <Video size={16} className="mr-2" />
                              Video
                          </button>
                      </div>
                  </div>
                  
                  <div className="flex justify-center gap-4">
                      {mode === 'photo' ? (
                          <Button onClick={capturePhoto} className="bg-white hover:bg-gray-100 text-black">
                              <Camera size={16} className="mr-2" />
                              Take Photo
                          </Button>
                      ) : (
                          <Button
                              onClick={isRecording ? stopRecording : startRecording}
                              variant={isRecording ? "destructive" : "default"}
                              className={isRecording ? "bg-red-500" : "bg-white text-black hover:bg-gray-100"}
                          >
                              {isRecording ? (
                                  <>
                                      <Square size={16} className="mr-2" />
                                      Stop Recording
                                  </>
                              ) : (
                                  <>
                                      <Video size={16} className="mr-2" />
                                      Start Recording
                                  </>
                              )}
                          </Button>
                      )}
                      <Button variant="secondary" onClick={onClose}>
                          Cancel
                      </Button>
                  </div>
              </div>
          </div>
      </div>
  );
};


const FileUploadArea = ({ onFileSelect }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Check if device is mobile or tablet
    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
      const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);
      
      // Additional check for iOS devices
      const isIOS = /ipad|iphone|ipod/.test(userAgent) && !window.MSStream;
      
      // Additional check for devices that can capture media
      const hasMediaCapture = 'capture' in document.createElement('input');
      
      setIsMobileOrTablet(isMobile || isTablet || isIOS || hasMediaCapture);
    };

    checkDevice();
  }, []);

  return (
    <div className="w-full space-y-4">

      {isMobileOrTablet ?(
        <div className="grid grid-cols-1 gap-4">       
          <div 
            className="border-2 border-dashed rounded-lg p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
             <div className="flex flex-col items-center justify-center">
                  <div className="flex space-x-2 mb-2">
                    <Upload className="w-8 h-8 text-gray-500" />
                    <Camera className="w-8 h-8 text-gray-500" />
                  </div>

              <p className="text-sm text-gray-500 text-center">Upload file or take photo/video</p>
              <p className="text-xs text-gray-400 text-center mt-1">
                Supports images, videos, 3D models docs and more
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={onFileSelect}
              />
            </div>
          </div>
        </div>
        ):(
        <div className="grid grid-cols-2 gap-4">       
          <div 
            className="border-2 border-dashed rounded-lg p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center">
              <Upload className="w-8 h-8 mb-2 text-gray-500" />
              <p className="text-sm text-gray-500 text-center">Upload files</p>
              <p className="text-xs text-gray-400 text-center mt-1">
                Supports images, videos, 3D models docs and more
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={onFileSelect}
              />
            </div>
          </div>

          <div 
            className="border-2 border-dashed rounded-lg p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer"
            onClick={() => setShowCamera(true)}
          >
            <div className="flex flex-col items-center justify-center">
              <Camera className="w-8 h-8 mb-2 text-gray-500" />
              <p className="text-sm text-gray-500 text-center">Take photo/video</p>
            </div>
          </div>
        </div>
        )
      }

      <Dialog open={showCamera} onOpenChange={setShowCamera}>
        <DialogContent className="max-w-4xl w-full p-0">
          <DialogTitle></DialogTitle>
          <CameraCapture 
            onCapture={onFileSelect}
            onClose={() => setShowCamera(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const FilePreview = ({ file }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      let mounted = true;
  
      const loadPreview = async () => {
        if (!file) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
          // If we have a blob URL from local upload, use it
          if (file.url?.startsWith('blob:')) {
            setPreviewUrl(file.url);
          }
          // Otherwise, download from S3
          else if (file.s3Key) {
            const downloadedFile = await ProjectService.downloadFileFromS3(file.s3Key);
            if (mounted) {
              const newUrl = URL.createObjectURL(downloadedFile);
              setPreviewUrl(newUrl);
            }
          } else {
            throw new Error('No valid source for file preview');
          }
        } catch (err) {
          console.error('Error loading preview:', err);
          if (mounted) {
            setError('Failed to load preview');
          }
        } finally {
          if (mounted) {
            setIsLoading(false);
          }
        }
      };
  
      loadPreview();
  
      return () => {
        mounted = false;
        // Cleanup blob URL if we created one
        if (previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }
      };
    }, [file]);
  
    if (!file) return null;
  
    // If we have a thumbnail but no URL yet (during loading), show the thumbnail
    if (file.thumbnail && !file.url) {
      return (
        <div className="flex items-center justify-center">
          <img 
            src={file.thumbnail} 
            alt={file.name}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>
      );
    }
  
    // Rest of FilePreview component remains the same...
    if (!file) return null;
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <p className="text-red-500">{error}</p>
        </div>
      );
    }
  
    const getPreviewContent = () => {
      const modelExtensions = ['.glb', '.gltf', '.obj', '.fbx', '.stl', '.3ds'];
      const isModelFile = modelExtensions.some(ext => 
        file.name?.toLowerCase().endsWith(ext) || 
        MODEL_FILE_TYPES.includes(file.type)
      );
  
      if (isModelFile) {
        return (
          <ThreeModelViewer 
            url={previewUrl} 
            fileType={file.name.substring(file.name.lastIndexOf('.'))}
          />
        );
      }
  
      if (file.name?.toLowerCase().endsWith('.dxf')) {
        return <DxfViewer url={previewUrl} />;
      }
  
      if (file.type.startsWith('image/')) {
        return (
          <img 
            src={previewUrl} 
            alt={file.name}
            className="max-w-full max-h-[80vh] object-contain"
          />
        );
      }
      
      if (file.type.startsWith('video/')) {
        return (
          <video 
            src={previewUrl}
            className="max-w-full max-h-[80vh]"
            controls
            autoPlay
            playsInline
          />
        );
      }
      
      if (file.type.startsWith('audio/')) {
        return (
          <audio 
            src={previewUrl}
            className="w-full"
            controls
          />
        );
      }
  
      if (file.type === 'application/pdf') {
        return (
          <iframe
            src={previewUrl}
            className="w-full h-[80vh]"
            title="PDF Preview"
          />
        );
      }
  
      // For other file types, show a download button
      return (
        <div className="text-center p-8">
          <div className="mb-4">
            {getFileIcon(file.type, file.name?.substring(file.name.lastIndexOf('.')))}
          </div>
          <p className="mb-4">{file.name}</p>
          <Button asChild>
            <a href={previewUrl} download={file.name}>Download File</a>
          </Button>
        </div>
      );
    };
  
    return (
      <div className="flex items-center justify-center">
        {getPreviewContent()}
      </div>
    );
};

const ThreeModelViewer = ({ url, fileType }) => {
    const mountRef = useRef(null);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      if (!mountRef.current) return;
  
      let animate;
      const cleanupFns = [];
  
      const init = async () => {
        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);
  
        // Camera setup
        const camera = new THREE.PerspectiveCamera(
          75,
          mountRef.current.clientWidth / mountRef.current.clientHeight,
          0.1,
          1000
        );
        camera.position.z = 5;
  
        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        mountRef.current.appendChild(renderer.domElement);
        cleanupFns.push(() => {
          renderer.dispose();
          mountRef.current?.removeChild(renderer.domElement);
        });
  
        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);
  
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(1, 1, 1);
        scene.add(directionalLight);
  
        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        cleanupFns.push(() => controls.dispose());
  
        // Load model based on file type
        try {
          let loader;
          if (fileType.toLowerCase().endsWith('.glb') || fileType.toLowerCase().endsWith('.gltf')) {
            loader = new GLTFLoader();
          } else if (fileType.toLowerCase().endsWith('.obj')) {
            loader = new OBJLoader();
          } else if (fileType.toLowerCase().endsWith('.fbx')) {
            loader = new FBXLoader();
          } else {
            throw new Error('Unsupported file format');
          }
  
          const model = await new Promise((resolve, reject) => {
            loader.load(
              url,
              resolve,
              undefined,
              reject
            );
          });
  
          // Handle different model formats
          const object = model.scene || model;
          scene.add(object);
  
          // Center and scale model
          const box = new THREE.Box3().setFromObject(object);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          object.scale.multiplyScalar(scale);
          
          object.position.sub(center.multiplyScalar(scale));
          camera.position.z = 5;
        } catch (err) {
          console.error('Error loading 3D model:', err);
          setError('Failed to load 3D model');
          return;
        }
  
        // Animation loop
        animate = () => {
          requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();
  
        // Handle resize
        const handleResize = () => {
          if (!mountRef.current) return;
          const width = mountRef.current.clientWidth;
          const height = mountRef.current.clientHeight;
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          renderer.setSize(width, height);
        };
        window.addEventListener('resize', handleResize);
        cleanupFns.push(() => window.removeEventListener('resize', handleResize));
      };
  
      init();
  
      return () => {
        if (animate) {
          cancelAnimationFrame(animate);
        }
        cleanupFns.forEach(fn => fn());
      };
    }, [url, fileType]);
  
    if (error) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <p className="text-red-500">{error}</p>
        </div>
      );
    }
  
    return <div ref={mountRef} className="w-full h-[400px]" />;
  };
  
const DxfViewer = ({ url }) => {
const mountRef = useRef(null);
const [error, setError] = useState(null);
const [loading, setLoading] = useState(true);

useEffect(() => {
    if (!mountRef.current) return;

    let animate;
    const cleanupFns = [];

    const createLine = (start, end) => {
    const points = [
        new THREE.Vector3(start.x, start.y, start.z || 0),
        new THREE.Vector3(end.x, end.y, end.z || 0)
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
    };

    const createArc = (center, radius, startAngle, endAngle) => {
    const curve = new THREE.EllipseCurve(
        center.x, center.y,
        radius, radius,
        startAngle, endAngle,
        false,
        0
    );
    const points = curve.getPoints(50);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
    };

    const createCircle = (center, radius) => {
    const curve = new THREE.EllipseCurve(
        center.x, center.y,
        radius, radius,
        0, 2 * Math.PI,
        false,
        0
    );
    const points = curve.getPoints(50);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
    };

    const createPolyline = (vertices) => {
    const points = vertices.map(v => new THREE.Vector3(v.x, v.y, v.z || 0));
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x000000 }));
    };

    const init = async () => {
    try {
        // Fetch DXF file content
        const response = await fetch(url);
        const dxfContent = await response.text();

        // Parse DXF
        const parser = new DxfParser();
        const dxf = parser.parseSync(dxfContent);

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf0f0f0);

        // Camera setup
        const camera = new THREE.PerspectiveCamera(
        45,
        mountRef.current.clientWidth / mountRef.current.clientHeight,
        0.1,
        1000
        );

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        mountRef.current.appendChild(renderer.domElement);
        cleanupFns.push(() => {
        renderer.dispose();
        mountRef.current?.removeChild(renderer.domElement);
        });

        // Create parent group for all entities
        const entityGroup = new THREE.Group();
        scene.add(entityGroup);

        // Process entities
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        dxf.entities.forEach(entity => {
        let object = null;

        switch (entity.type) {
            case 'LINE':
            object = createLine(entity.start, entity.end);
            minX = Math.min(minX, entity.start.x, entity.end.x);
            minY = Math.min(minY, entity.start.y, entity.end.y);
            maxX = Math.max(maxX, entity.start.x, entity.end.x);
            maxY = Math.max(maxY, entity.start.y, entity.end.y);
            break;

            case 'CIRCLE':
            object = createCircle(entity.center, entity.radius);
            minX = Math.min(minX, entity.center.x - entity.radius);
            minY = Math.min(minY, entity.center.y - entity.radius);
            maxX = Math.max(maxX, entity.center.x + entity.radius);
            maxY = Math.max(maxY, entity.center.y + entity.radius);
            break;

            case 'ARC':
            object = createArc(
                entity.center,
                entity.radius,
                entity.startAngle * Math.PI / 180,
                entity.endAngle * Math.PI / 180
            );
            minX = Math.min(minX, entity.center.x - entity.radius);
            minY = Math.min(minY, entity.center.y - entity.radius);
            maxX = Math.max(maxX, entity.center.x + entity.radius);
            maxY = Math.max(maxY, entity.center.y + entity.radius);
            break;

            case 'LWPOLYLINE':
            case 'POLYLINE':
            if (entity.vertices?.length > 0) {
                object = createPolyline(entity.vertices);
                entity.vertices.forEach(vertex => {
                minX = Math.min(minX, vertex.x);
                minY = Math.min(minY, vertex.y);
                maxX = Math.max(maxX, vertex.x);
                maxY = Math.max(maxY, vertex.y);
                });
            }
            break;
        }

        if (object) {
            entityGroup.add(object);
        }
        });

        // Center and scale the drawing
        if (minX !== Infinity && minY !== Infinity) {
        const width = maxX - minX;
        const height = maxY - minY;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // Move to center
        entityGroup.position.set(-centerX, -centerY, 0);

        // Scale to fit view
        const scale = Math.min(
            8 / width,
            8 / height
        );
        entityGroup.scale.set(scale, scale, scale);
        }

        // Add grid
        const gridHelper = new THREE.GridHelper(20, 20);
        gridHelper.rotation.x = Math.PI / 2;
        scene.add(gridHelper);

        // Position camera
        camera.position.set(0, 0, 10);

        // Add controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.screenSpacePanning = true;
        cleanupFns.push(() => controls.dispose());

        // Animation loop
        animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
        };
        animate();

        // Handle resize
        const handleResize = () => {
        if (!mountRef.current) return;
        const width = mountRef.current.clientWidth;
        const height = mountRef.current.clientHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        };
        window.addEventListener('resize', handleResize);
        cleanupFns.push(() => window.removeEventListener('resize', handleResize));

        setLoading(false);
    } catch (err) {
        console.error('Error loading DXF:', err);
        setError('Failed to load DXF file');
        setLoading(false);
    }
    };

    init();

    return () => {
    if (animate) {
        cancelAnimationFrame(animate);
    }
    cleanupFns.forEach(fn => fn());
    };
}, [url]);

if (loading) {
    return (
    <div className="flex items-center justify-center h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
    </div>
    );
}

if (error) {
    return (
    <div className="flex items-center justify-center h-[400px]">
        <p className="text-red-500">{error}</p>
    </div>
    );
}

return <div ref={mountRef} className="w-full h-[400px]" />;
};

const getFileIcon = (mimeType, extension) => {
    // Check file extension for 3D models (since MIME types aren't always reliable)
    const modelExtensions = ['.glb', '.gltf', '.obj', '.fbx', '.stl', '.3ds'];
    if (MODEL_FILE_TYPES.includes(mimeType) || 
        modelExtensions.some(ext => extension?.toLowerCase().endsWith(ext))) {
      return <Cuboid />;
    }
    
    if (mimeType.startsWith('image/')) return <ImageOff />;
    if (mimeType.startsWith('video/')) return <Video />;
    if (mimeType.startsWith('audio/')) return <Music />;
    if (mimeType.startsWith('text/')) return <FileText />;
    if (mimeType.includes('pdf')) return <FileText />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileText />;
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <FileText />;
    if (mimeType.includes('compressed') || mimeType.includes('zip') || mimeType.includes('rar')) return <Archive />;
    if (mimeType.includes('code') || mimeType.includes('javascript') || mimeType.includes('json')) return <Code />;
    return <File />;
  };

const MODEL_FILE_TYPES = [
    'model/gltf-binary',
    'model/gltf+json',
    'model/obj',
    'model/fbx',
    'application/octet-stream',
    'application/dxf',
    'application/x-dxf',
    'image/vnd.dxf',
];

const FileUploadCard = ({ 
  title = "Upload Files",
  projectId,
  onFilesChange,
  initialFiles = [],
  cardClassName = "",
  defaultCollapsed = false
}) => {
    const [editingNotes, setEditingNotes] = useState({});
    const [selectedPreview, setSelectedPreview] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [localPreviews, setLocalPreviews] = useState({});
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [fileItems, setFileItems] = useState(initialFiles);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    
    useEffect(() => {
        const saveChangesToDB = async () => {
            if (unsavedChanges && projectId) {
                try {
                  await ProjectService.updateProject(projectId, {
                      fileItems: fileItems
                  });
                    setUnsavedChanges(false);
                    if (onFilesChange) {
                        onFilesChange(fileItems);
                    }
                } catch (error) {
                    console.error('Error saving file changes to DB:', error);
                }
            }
        };

        saveChangesToDB();
    }, [unsavedChanges, fileItems, projectId, onFilesChange]);

    const handleFileUpload = async (e) => {
        const newFiles = Array.from(e.target.files);
        
        for (const file of newFiles) {
            const fileId = String(Date.now() + Math.random());
            
            try {
                const thumbnail = await generateThumbnail(file);
                
                setFileItems(prev => [...prev, {
                    id: fileId,
                    name: file.name,
                    type: file.type,
                    size: String(file.size),
                    isUploading: true,
                    thumbnail,
                    notes: '',
                    section: 'general',
                    createdAt: new Date().toISOString(),
                }]);
                
                const { s3Key, s3Url } = await ProjectService.uploadFileToS3(
                    file, 
                    projectId,
                    () => {}
                );
                
                setFileItems(prev => {
                    const updatedItems = prev.map(item => 
                        item.id === fileId ? {
                            ...item,
                            isUploading: false,
                            thumbnail,
                            s3Key,
                            s3Url
                        } : item
                    );
                    return updatedItems;
                });
                setUnsavedChanges(true);
                
                const previewUrl = URL.createObjectURL(file);
                setLocalPreviews(prev => ({
                    ...prev,
                    [fileId]: {
                        url: previewUrl,
                        type: file.type
                    }
                }));

            } catch (error) {
                console.error('Error processing file:', error);
                setFileItems(prev => prev.filter(item => item.id !== fileId));
            }
        }
    };

    const removeFile = async (id) => {
        try {
            const fileItem = fileItems.find(item => item.id === id);
            
            if (fileItem?.s3Key) {
                await ProjectService.deleteFileFromS3(fileItem.s3Key);
            }
        
            if (localPreviews[id]?.url) {
                URL.revokeObjectURL(localPreviews[id].url);
                setLocalPreviews(prev => {
                    const updated = { ...prev };
                    delete updated[id];
                    return updated;
                });
            }
            
            setFileItems(prev => prev.filter(item => item.id !== id));
            setUnsavedChanges(true);
        } catch (error) {
            console.error('Error removing file:', error);
        }
    };

    const handleNoteSave = (id) => {
        const note = editingNotes[id];
        if (note === undefined) return;

        setFileItems(prev => prev.map(item => 
            item.id === id ? { ...item, notes: note } : item
        ));
        setUnsavedChanges(true);
        
        setEditingNotes(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    };

    const handleNoteEdit = (id, note) => {
        setEditingNotes(prev => ({
        ...prev,
        [id]: note
        }));
    };

    const handleNoteCancel = (id) => {
        setEditingNotes(prev => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
        });
    };

    const openPreview = async (fileItem) => {
    let previewUrl = localPreviews[fileItem.id]?.url;
    
    if (!previewUrl) {
      setIsPreviewLoading(true);
      setSelectedPreview({
        type: fileItem.type,
        name: fileItem.name,
        s3Key: fileItem.s3Key,
        thumbnail: fileItem.thumbnail // Include thumbnail in preview data
      });
      setIsPreviewOpen(true);

      try {
        const file = await getFileForPreview(fileItem);
        if (file) {
          previewUrl = URL.createObjectURL(file);
          setLocalPreviews(prev => ({
            ...prev,
            [fileItem.id]: {
              url: previewUrl,
              type: file.type
            }
          }));
          setSelectedPreview(prev => ({
            ...prev,
            url: previewUrl
          }));
        }
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        setIsPreviewLoading(false);
      }
    } else {
      setSelectedPreview({ 
        type: fileItem.type,
        url: previewUrl,
        name: fileItem.name,
        s3Key: fileItem.s3Key,
        thumbnail: fileItem.thumbnail // Include thumbnail in preview data
      });
      setIsPreviewOpen(true);
    }
    };

    const getFileForPreview = async (fileItem) => {
        try {
        if (fileItem.s3Key) {
            return await ProjectService.downloadFileFromS3(fileItem.s3Key);
        }
        return null;
        } catch (error) {
        console.error('Error getting file for preview:', error);
        return null;
        }
    };

    const renderFilePreview = (fileItem) => {
        if (fileItem.isUploading) {
        return (
            <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
            <span className="text-sm text-gray-600 mt-2">Uploading...</span>
            </div>
        );
        }

        return (
        <div 
            className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 group relative cursor-pointer"
            onClick={() => openPreview(fileItem)}
        >
            {fileItem.thumbnail ? (
            <>
                <img 
                src={fileItem.thumbnail} 
                alt={fileItem.name} 
                className="w-full h-full object-cover"
                />
                {fileItem.type.startsWith('video/') && (
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                    <Play className="w-8 h-8 text-white" />
                </div>
                )}
            </>
            ) : (
            <div className="w-full h-full flex items-center justify-center">
                {getFileIcon(fileItem.type, fileItem.name?.substring(fileItem.name.lastIndexOf('.')))}
            </div>
            )}
            
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <Maximize2 className="text-white opacity-0 group-hover:opacity-100 w-6 h-6" />
            </div>
        </div>
        );
    };

    const renderFileItem = (item) => (
        <div key={item.id} className="p-4 border rounded-lg">
        <div className="flex justify-between items-center mb-2">
            <span className="font-medium">{item.name}</span>
            {!item.isUploading && item.section === 'general' && (
            <Button
                variant="destructive"
                size="sm"
                onClick={() => removeFile(item.id)}
            >
                <Trash2 className="w-4 h-4" />
            </Button>
            )}
        </div>
        <div className="flex gap-4">
            {renderFilePreview(item)}
            <div className="flex-1">
            {!item.isUploading ? (
                <>
                <Textarea
                    placeholder="Add notes or description..."
                    value={editingNotes[item.id] !== undefined ? editingNotes[item.id] : item.notes}
                    onChange={(e) => handleNoteEdit(item.id, e.target.value)}
                    className="flex-1 mb-2"
                />
                {editingNotes[item.id] !== undefined && (
                    <div className="flex justify-end gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingNotes(prev => {
                        const newState = { ...prev };
                        delete newState[item.id];
                        return newState;
                        })}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => handleNoteSave(item.id)}
                    >
                        Save Notes
                    </Button>
                    </div>
                )}
                </>
            ) : (
                <div className="h-[120px] flex items-center justify-center text-gray-500">
                Processing file...
                </div>
            )}
            </div>
        </div>
        </div>
    );

    return (
        <Card className={cardClassName}>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{title}</CardTitle>
                {fileItems.length > 0 && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="ml-2"
                >
                    {isCollapsed ? (
                    <>
                        <span className="mr-2 text-sm">Show Files ({fileItems.length})</span>
                        <ChevronDown className="h-4 w-4" />
                    </>
                    ) : (
                    <>
                        <span className="mr-2 text-sm">Hide Files</span>
                        <ChevronUp className="h-4 w-4" />
                    </>
                    )}
                </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                <FileUploadArea onFileSelect={handleFileUpload} />
                
                <AnimatePresence>
                {!isCollapsed && fileItems.length > 0 && (
                    <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                    >
                    <div className="space-y-4 pt-4">
                        {fileItems.map(renderFileItem)}
                    </div>
                    </motion.div>
                )}
                </AnimatePresence>

                {isCollapsed && fileItems.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="pt-2"
                >
                    <div className="flex flex-wrap gap-2">
                    {fileItems.slice(0, 5).map(file => (
                        <div
                        key={file.id}
                        className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center relative group"
                        onClick={() => setIsCollapsed(false)}
                        >
                        {file.thumbnail ? (
                            <img 
                            src={file.thumbnail} 
                            alt={file.name}
                            className="w-full h-full object-cover"
                            />
                        ) : (
                            getFileIcon(file.type, file.name)
                        )}
                        </div>
                    ))}
                    {fileItems.length > 5 && (
                        <div 
                        className="w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200"
                        onClick={() => setIsCollapsed(false)}
                        >
                        <span className="text-sm text-gray-600">+{fileItems.length - 5}</span>
                        </div>
                    )}
                    </div>
                </motion.div>
                )}

                <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <DialogContent className="max-w-4xl w-full p-6">
                    <DialogHeader>
                        <DialogTitle className="sr-only">File Preview</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    <DialogTitle></DialogTitle>
                    {/* <FilePreview file={selectedPreview} /> */}
                    <div className="p-6">
                        {isPreviewLoading ? (
                        <div className="flex flex-col items-center justify-center h-[400px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            <span className="mt-2">Loading preview...</span>
                        </div>
                        ) : (
                        <FilePreview file={selectedPreview} />
                        )}
                    </div>
                </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default FileUploadCard;